import { ThemeProvider, CssBaseline, createTheme } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { AuthProvider } from "./context/AuthContext";

import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import PortalRouteTracker from "./components/analytics/PortalRouteTracker";
import ConversationsPage from "./pages/ConversationsPage";
import Bookings from "./pages/Bookings";
import Settings from "./pages/Settings";
import GoogleCallback from "./pages/GoogleCallback";
import MicrosoftCallback from "./pages/MicrosoftCallback";
import MetaCallback from "./pages/MetaCallback";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import OnboardingWizard from "./pages/OnboardingWizard";
import LinkProviderPage from "./pages/LinkProviderPage";
import AccountLinkRequiredPage from "./pages/AccountLinkRequiredPage";
import EmailLinkCallbackPage from "./pages/EmailLinkCallbackPage";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import CancelBooking from "./pages/CancelBooking";
import ContactsPage from "./pages/ContactsPage";
import Overview from "./pages/Overview";
import CalendarPage from "./pages/CalendarPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import AdminPage from "./pages/admin/AdminPage";
import BillingPage from "./pages/BillingPage";
import BillingCheckoutConfirmationPage from "./pages/BillingCheckoutConfirmationPage";
import { queryClient } from "./queryClient";

const PJS = '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif';

const theme = createTheme({
  palette: {
    primary: { main: "#C9734F", light: "#E8946E", dark: "#A85E3E" },
    secondary: { main: "#0F172A", light: "#334155", dark: "#08111F" },
    background: { default: "#F4F7F9", paper: "#FFFFFF" },
    text: { primary: "#0F172A", secondary: "#64748B" },
    divider: "#E7EDF2",
  },
  typography: {
    fontFamily: PJS,
    htmlFontSize: 16,
    h1: { fontFamily: PJS, fontSize: "1.75rem",   fontWeight: 700, letterSpacing: "-0.02em",  lineHeight: 1.2  },
    h2: { fontFamily: PJS, fontSize: "1.375rem",  fontWeight: 700, letterSpacing: "-0.018em", lineHeight: 1.25 },
    h3: { fontFamily: PJS, fontSize: "1.125rem",  fontWeight: 600, letterSpacing: "-0.012em", lineHeight: 1.3  },
    h4: { fontFamily: PJS, fontSize: "1rem",      fontWeight: 600, letterSpacing: "-0.008em", lineHeight: 1.35 },
    h5: { fontFamily: PJS, fontSize: "0.9375rem", fontWeight: 600, letterSpacing: "-0.004em", lineHeight: 1.4  },
    h6: { fontFamily: PJS, fontSize: "0.875rem",  fontWeight: 600, letterSpacing: 0,          lineHeight: 1.4  },
    subtitle1: { fontFamily: PJS, fontSize: "0.9375rem", fontWeight: 500, letterSpacing: "0.004em", lineHeight: 1.5 },
    subtitle2: { fontFamily: PJS, fontSize: "0.8125rem", fontWeight: 500, letterSpacing: "0.004em", lineHeight: 1.5 },
    body1:     { fontFamily: PJS, fontSize: "0.875rem",  fontWeight: 400, letterSpacing: 0,          lineHeight: 1.6 },
    body2:     { fontFamily: PJS, fontSize: "0.8125rem", fontWeight: 400, letterSpacing: 0,          lineHeight: 1.55 },
    button:    { fontFamily: PJS, fontSize: "0.875rem",  fontWeight: 600, letterSpacing: "0.008em"  },
    caption:   { fontFamily: PJS, fontSize: "0.75rem",   fontWeight: 400, letterSpacing: "0.02em",  lineHeight: 1.4 },
    overline:  { fontFamily: PJS, fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em",  lineHeight: 1.4, textTransform: "uppercase" as const },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "10px",
        },
        contained: {
          backgroundColor: "#C9734F",
          color: "#fff",
          boxShadow: "none",
          "&:hover": { backgroundColor: "#b8613f", boxShadow: "none" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <AuthProvider>
              <PortalRouteTracker />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/onboarding" element={<OnboardingWizard />} />
                <Route path="/link-provider" element={<LinkProviderPage />} />
                <Route path="/account-link-required" element={<AccountLinkRequiredPage />} />
                <Route path="/auth/email/callback" element={<EmailLinkCallbackPage />} />
                <Route path="/start-registration" element={<Navigate to="/login" replace />} />
                <Route path="/register" element={<Login />} />
                <Route path="/cancel" element={<CancelBooking />} />

                <Route element={<Layout />}>
                  {/* Primary nav routes */}
                  <Route path="/" element={<ProtectedRoute><Overview /></ProtectedRoute>} />
                  <Route path="/conversations" element={<ProtectedRoute><ConversationsPage /></ProtectedRoute>} />
                  <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
                  <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
                  <Route path="/customers" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
                  <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
                  <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
                  <Route path="/billing/checkout/success" element={<Navigate to="/billing?checkout=success" replace />} />
                  <Route path="/billing/checkout/canceled" element={<Navigate to="/billing?checkout=canceled" replace />} />
                  <Route
                    path="/billing/checkout/confirm"
                    element={<ProtectedRoute><BillingCheckoutConfirmationPage /></ProtectedRoute>}
                  />
                  <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />

                  {/* Legacy redirects → settings tabs */}
                  <Route path="/account" element={<Navigate to="/settings?tab=account" replace />} />
                  <Route path="/branding" element={<Navigate to="/settings?tab=branding" replace />} />
                  <Route path="/staff" element={<Navigate to="/settings?tab=staff" replace />} />
                  <Route path="/notifications" element={<Navigate to="/settings?tab=notifications" replace />} />

                  <Route path="/services" element={<Navigate to="/settings?tab=services" replace />} />

                  {/* Legacy chat redirect */}
                  <Route path="/chathistory" element={<Navigate to="/conversations" replace />} />
                  <Route path="/contacts" element={<Navigate to="/customers" replace />} />

                  {/* Auth callbacks */}
                  <Route path="/auth/google/callback" element={<GoogleCallback />} />
                  <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
                  <Route path="/auth/meta/callback" element={<MetaCallback />} />

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </AuthProvider>
          </Router>
        </ThemeProvider>
      </LocalizationProvider>
    </QueryClientProvider>
  );
}

export default App;
